import { useEffect, useCallback, useState } from "react";
import axios from "axios";

const SCRIPT_ID = "google-identity-script";

/**
 * Renders Google Sign-In button. On success, stores token/user in localStorage and calls onSuccess().
 */
export default function GoogleAuthPanel({
  buttonId,
  variant = "signin_with",
  isDarkMode,
  onSuccess,
  onError,
}) {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
  const [internalLoading, setInternalLoading] = useState(false);

  const handleCredential = useCallback(
    async (credentialResponse) => {
      setInternalLoading(true);
      try {
        const response = await axios.post("/api/google-login/", {
          id_token: credentialResponse.credential,
        });
        if (response.data.success) {
          localStorage.setItem("token", response.data.token);
          localStorage.setItem("userEmail", response.data.email);
          const displayName =
            response.data.name || response.data.email.split("@")[0];
          localStorage.setItem("userName", displayName);
          window.dispatchEvent(new Event("cliptranscript-auth-changed"));
          onSuccess?.();
        } else {
          onError?.("Google sign in failed");
        }
      } catch (err) {
        console.error("Google auth error:", err);
        onError?.("Google sign in failed. Please try again.");
      } finally {
        setInternalLoading(false);
      }
    },
    [onSuccess, onError]
  );

  useEffect(() => {
    if (!googleClientId) return;

    const initGoogleButton = () => {
      if (!window.google?.accounts?.id) {
        setTimeout(initGoogleButton, 300);
        return;
      }
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleCredential,
      });
      const button = document.getElementById(buttonId);
      if (button) {
        button.innerHTML = "";
        window.google.accounts.id.renderButton(button, {
          theme: "outline",
          size: "large",
          width: "100%",
          text: variant,
          locale: "en",
        });
      }
    };

    const existingScript = document.getElementById(SCRIPT_ID);
    if (existingScript) {
      initGoogleButton();
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = "https://accounts.google.com/gsi/client?hl=en";
    script.async = true;
    script.defer = true;
    script.onload = initGoogleButton;
    document.body.appendChild(script);
  }, [googleClientId, variant, buttonId, handleCredential]);

  if (!googleClientId) {
    return (
      <p style={{ color: "#ef4444", textAlign: "center", margin: 0 }}>
        Google Client ID is not configured.
      </p>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <div
        id={buttonId}
        style={{ display: "flex", justifyContent: "center", width: "100%" }}
      />
      {internalLoading && (
        <p
          style={{
            textAlign: "center",
            marginTop: "12px",
            marginBottom: 0,
            color: isDarkMode ? "#9ca3af" : "#6b7280",
            fontSize: "14px",
          }}
        >
          Signing in…
        </p>
      )}
    </div>
  );
}
